"use client";

import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
export type ModelParameterType = "string" | "number" | "boolean" | "range";

export interface ModelParameterItem {
    name: string;
    type: ModelParameterType;
    default: string | number | boolean;
    min?: number;
    max?: number;
}

interface ModelParameterEditorProps {
    parameters: ModelParameterItem[];
    onChange: (parameters: ModelParameterItem[]) => void;
    readOnly?: boolean;
}

function coerceDefault(
    type: ModelParameterType,
    value: string | number | boolean
): string | number | boolean {
    if (type === "number" || type === "range") {
        const n = Number(value);
        return Number.isFinite(n) ? n : 0;
    }
    if (type === "boolean") {
        if (typeof value === "boolean") return value;
        if (typeof value === "string")
            return value.toLowerCase() === "true" || value === "1";
        return Boolean(value);
    }
    return typeof value === "string" ? value : String(value ?? "");
}

export function ModelParameterEditor({
    parameters,
    onChange,
    readOnly = false,
}: ModelParameterEditorProps) {
    const updateParam = (index: number, updates: Partial<ModelParameterItem>) => {
        const next = [...parameters];
        next[index] = { ...next[index], ...updates };
        onChange(next);
    };

    const addParam = () => {
        onChange([
            ...parameters,
            {
                name: "",
                type: "string",
                default: "",
            },
        ]);
    };

    const removeParam = (index: number) => {
        const next = parameters.filter((_, i) => i !== index);
        onChange(next.length ? next : []);
    };

    const handleDefaultChange = (index: number, raw: string) => {
        const param = parameters[index];
        const value = coerceDefault(param.type, raw);
        updateParam(index, { default: value });
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Model parameters</Label>
                {!readOnly && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addParam}
                        className="h-8"
                    >
                        <Plus size={14} className="mr-1" />
                        Add parameter
                    </Button>
                )}
            </div>

            {parameters.length === 0 ? (
                <p className="text-sm text-slate-500 py-4 border border-dashed border-slate-200 rounded-lg text-center">
                    No parameters. Add one to define type, default value, or range.
                </p>
            ) : (
                <ul className="space-y-3">
                    {parameters.map((param, index) => (
                        <li
                            key={index}
                            className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 bg-slate-50/50 p-3"
                        >
                            <div className="flex-1 min-w-[120px] space-y-1">
                                <Label className="text-xs text-slate-500">
                                    Name
                                </Label>
                                <Input
                                    value={param.name}
                                    onChange={(e) =>
                                        updateParam(index, {
                                            name: e.target.value,
                                        })
                                    }
                                    disabled={readOnly}
                                    placeholder="e.g. temperature"
                                    className="h-9 font-mono text-sm"
                                />
                            </div>
                            <div className="w-[120px] space-y-1">
                                <Label className="text-xs text-slate-500">
                                    Type
                                </Label>
                                <Select
                                    value={param.type}
                                    onValueChange={(v) => {
                                        const newType = v as ModelParameterType;
                                        updateParam(index, {
                                            type: newType,
                                            default: coerceDefault(
                                                newType,
                                                param.default
                                            ),
                                        });
                                    }}
                                    disabled={readOnly}
                                >
                                    <SelectTrigger className="h-9">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="string">
                                            String
                                        </SelectItem>
                                        <SelectItem value="number">
                                            Number
                                        </SelectItem>
                                        <SelectItem value="boolean">
                                            Boolean
                                        </SelectItem>
                                        <SelectItem value="range">
                                            Range
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            {(param.type === "number" ||
                                param.type === "range") && (
                                <>
                                    {param.type === "range" && (
                                        <>
                                            <div className="w-[80px] space-y-1">
                                                <Label className="text-xs text-slate-500">
                                                    Min
                                                </Label>
                                                <Input
                                                    type="number"
                                                    value={
                                                        param.min ??
                                                        ""
                                                    }
                                                    onChange={(e) => {
                                                        const v = e.target
                                                            .value;
                                                        updateParam(index, {
                                                            min:
                                                                v === ""
                                                                    ? undefined
                                                                    : Number(v),
                                                        });
                                                    }}
                                                    disabled={readOnly}
                                                    className="h-9"
                                                />
                                            </div>
                                            <div className="w-[80px] space-y-1">
                                                <Label className="text-xs text-slate-500">
                                                    Max
                                                </Label>
                                                <Input
                                                    type="number"
                                                    value={
                                                        param.max ??
                                                        ""
                                                    }
                                                    onChange={(e) => {
                                                        const v = e.target
                                                            .value;
                                                        updateParam(index, {
                                                            max:
                                                                v === ""
                                                                    ? undefined
                                                                    : Number(v),
                                                        });
                                                    }}
                                                    disabled={readOnly}
                                                    className="h-9"
                                                />
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                            <div className="flex-1 min-w-[100px] space-y-1">
                                <Label className="text-xs text-slate-500">
                                    Default
                                </Label>
                                {param.type === "boolean" ? (
                                    <Select
                                        value={String(param.default)}
                                        onValueChange={(v) =>
                                            updateParam(index, {
                                                default: v === "true",
                                            })
                                        }
                                        disabled={readOnly}
                                    >
                                        <SelectTrigger className="h-9">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="true">
                                                true
                                            </SelectItem>
                                            <SelectItem value="false">
                                                false
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                ) : (
                                    <Input
                                        type={
                                            param.type === "number" ||
                                            param.type === "range"
                                                ? "number"
                                                : "text"
                                        }
                                        value={String(param.default)}
                                        onChange={(e) =>
                                            handleDefaultChange(
                                                index,
                                                e.target.value
                                            )
                                        }
                                        disabled={readOnly}
                                        placeholder="Default value"
                                        className="h-9"
                                    />
                                )}
                            </div>
                            {!readOnly && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeParam(index)}
                                    className="h-9 w-9 p-0 text-red-500 hover:text-red-600 hover:bg-red-50 shrink-0"
                                >
                                    <Trash2 size={16} />
                                </Button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

/**
 * Convert a flat parameters record + optional meta into ModelParameterItem[].
 * Meta can hold { type?, min?, max? } per key.
 */
export function parametersFromRecord(
    record: Record<string, unknown> | null | undefined,
    meta?: Record<string, { type?: ModelParameterType; min?: number; max?: number }>
): ModelParameterItem[] {
    if (!record || typeof record !== "object") return [];
    const entries = Object.entries(record).filter(
        ([key]) => !key.startsWith("_")
    );
    if (entries.length === 0) return [];
    return entries.map(([name, value]) => {
        const m = meta?.[name];
        const type = m?.type ?? inferType(value);
        return {
            name,
            type,
            default: coerceDefault(type, value as string | number | boolean),
            min: m?.min,
            max: m?.max,
        };
    });
}

function inferType(value: unknown): ModelParameterType {
    if (typeof value === "boolean") return "boolean";
    if (typeof value === "number") return "number";
    return "string";
}

/**
 * Convert ModelParameterItem[] back to a flat record and optional meta for persistence.
 */
export function parametersToRecord(
    items: ModelParameterItem[]
): {
    parameters: Record<string, unknown>;
    parameterMeta?: Record<
        string,
        { type: ModelParameterType; min?: number; max?: number }
    >;
} {
    const parameters: Record<string, unknown> = {};
    const parameterMeta: Record<
        string,
        { type: ModelParameterType; min?: number; max?: number }
    > = {};
    for (const p of items) {
        if (!p.name.trim()) continue;
        if (p.name.startsWith("_")) continue;
        parameters[p.name] = p.default;
        parameterMeta[p.name] = {
            type: p.type,
            ...(p.type === "range" && {
                min: p.min,
                max: p.max,
            }),
        };
    }
    return { parameters, parameterMeta };
}
